/* Copyright (c) 2025 Xperi. All Rights Reserved.  */

import { PreviewGroup } from 'client-code/previews';
import { VodItemKernelType } from 'client-code/vod-browse';

import { VodCompnentsType, VodScreenType } from './VodPreviewProps';

export type AcceptablePreviewType =
    | 'ProgramPreview'
    | 'EpisodePreview'
    | 'MoviePreview'
    | 'UnifiedCollectionPreview';

export const COMPONENT_VISIBILITY: {
    [screen in VodScreenType]: {
        [type in AcceptablePreviewType]?: VodCompnentsType[];
    };
} = {
    [VodScreenType.VOD_MAIN_SCREEN]: {
        ProgramPreview: [
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.Source,
        ],
        EpisodePreview: [
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.EpisodeInfo,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.Source,
        ],
        MoviePreview: [
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.Duration,
            VodCompnentsType.StarRating,
            VodCompnentsType.Source,
        ],
        UnifiedCollectionPreview: [
            VodCompnentsType.Title,
            VodCompnentsType.Description,
            VodCompnentsType.Source,
        ],
    },
    [VodScreenType.VOD_GALLERY_SCREEN]: {
        ProgramPreview: [
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.Cast,
            VodCompnentsType.Duration,
            VodCompnentsType.Source,
        ],
        EpisodePreview: [
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.EpisodeInfo,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.Cast,
            VodCompnentsType.Source,
        ],
        MoviePreview: [
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.DurationWithStarRating,
            VodCompnentsType.Cast,
            VodCompnentsType.Source,
        ],
        UnifiedCollectionPreview: [
            VodCompnentsType.Title,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.Duration,
            VodCompnentsType.Cast,
            VodCompnentsType.Source,
        ],
    },
    [VodScreenType.VOD_MY_RENTAL_LIST_SCREEN]: {
        ProgramPreview: [
            VodCompnentsType.ContentRentalImage,
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.EpisodeInfo,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.Cast,
            VodCompnentsType.Source,
        ],
        EpisodePreview: [
            VodCompnentsType.ContentRentalImage,
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.EpisodeInfo,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.Cast,
            VodCompnentsType.Source,
        ],
        MoviePreview: [
            VodCompnentsType.ContentRentalImage,
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.Cast,
            VodCompnentsType.Source,
        ],
        UnifiedCollectionPreview: [
            VodCompnentsType.ContentRentalImage,
            VodCompnentsType.Title,
            VodCompnentsType.EpisodeInfo,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.Cast,
            VodCompnentsType.Source,
        ],
    },
    [VodScreenType.ON_DEMAND_EPISODES_SCREEN]: {
        EpisodePreview: [
            VodCompnentsType.ContentRentalImage,
            VodCompnentsType.StatusMessage,
            VodCompnentsType.Title,
            VodCompnentsType.EpisodeInfo,
            VodCompnentsType.Description,
            VodCompnentsType.ShowAttributes,
            VodCompnentsType.RatingGenres,
            VodCompnentsType.Cast,
            VodCompnentsType.Duration,
            VodCompnentsType.Source,
        ],
    },
    [VodScreenType.VOD_PROGRAM_SCREEN]: {
        ProgramPreview: [],
        EpisodePreview: [],
        MoviePreview: [],
        UnifiedCollectionPreview: [],
    },
    [VodScreenType.VOD_SERIES_SCREEN]: {
        UnifiedCollectionPreview: [],
    },
};

export const PREVIEW_GROUP_MAPPING: {
    [screen in VodScreenType]: (kernelType: VodItemKernelType) => PreviewGroup[];
} = {
    [VodScreenType.VOD_MAIN_SCREEN]: (kernelType) =>
        kernelType === VodItemKernelType.UnifiedCollectionVodItemKernel
            ? [PreviewGroup.Description, PreviewGroup.OnDemand]
            : [
                  PreviewGroup.VodStatus,
                  PreviewGroup.Description,
                  PreviewGroup.OnDemand,
                  PreviewGroup.Metadata,
              ],

    [VodScreenType.VOD_GALLERY_SCREEN]: (kernelType) =>
        kernelType === VodItemKernelType.UnifiedCollectionVodItemKernel
            ? [
                  PreviewGroup.Description,
                  PreviewGroup.OnDemand,
                  PreviewGroup.Metadata,
                  PreviewGroup.Credits,
              ]
            : [
                  PreviewGroup.VodStatus,
                  PreviewGroup.Description,
                  PreviewGroup.OnDemand,
                  PreviewGroup.Metadata,
                  PreviewGroup.Credits,
              ],

    [VodScreenType.VOD_MY_RENTAL_LIST_SCREEN]: () => [
        PreviewGroup.VodStatus,
        PreviewGroup.Description,
        PreviewGroup.OnDemand,
        PreviewGroup.Metadata,
        PreviewGroup.Credits,
    ],

    [VodScreenType.VOD_PROGRAM_SCREEN]: () => [
        PreviewGroup.Description,
        PreviewGroup.Metadata,
        PreviewGroup.OnDemand,
        PreviewGroup.VodStatus,
        PreviewGroup.Casts,
    ],

    [VodScreenType.VOD_SERIES_SCREEN]: () => [
        PreviewGroup.VodStatus,
        PreviewGroup.Description,
        PreviewGroup.OnDemand,
        PreviewGroup.Metadata,
    ],

    [VodScreenType.ON_DEMAND_EPISODES_SCREEN]: () => [
        PreviewGroup.VodStatus,
        PreviewGroup.Description,
        PreviewGroup.OnDemand,
        PreviewGroup.Metadata,
        PreviewGroup.Credits,
    ],
};

export const ESTIMATED_LINE_HEIGHT = 28;

export const ESTIMATED_OPTIONAL_COMPONENT_LINES: Record<'genres' | 'duration' | 'actors', number> =
    {
        genres: 1,
        duration: 1,
        actors: 1,
    };

export const FIXED_COMPONENT_KEYS: VodCompnentsType[] = [
    VodCompnentsType.ContentRentalImage,
    VodCompnentsType.StatusMessage,
    VodCompnentsType.Title,
    VodCompnentsType.EpisodeInfo,
    VodCompnentsType.TrickplayRestriction,
];
